import { z } from "zod"
import { SurveyDataSchema } from "./auth"

export const SellerShopStatusSchema = z.enum([
  "filling",
  "on_moderation",
  "active",
  "blocked",
])
export type SellerShopStatus = z.infer<typeof SellerShopStatusSchema>

/** A seller's shop as seen by admin moderation; id is the seller user id. */
export const SellerShopSchema = z.object({
  id: z.string(),
  /** Storefront catalog id this seller owns, or null before publication. */
  shopId: z.string().nullable(),
  shopName: z.string(),
  sellerName: z.string(),
  email: z.string(),
  phone: z.string(),
  role: z.string(),
  status: SellerShopStatusSchema,
  onboardingStatus: z.string(),
  submittedAt: z.string().nullable(),
  createdAt: z.string(),
})
export type SellerShop = z.infer<typeof SellerShopSchema>

export const ShopProfileSchema = z.object({
  shopName: z.string(),
  foundingDate: z.string(),
  brandDescription: z.string(),
  mainProduct: z.string(),
  role: z.string(),
  roleCustom: z.string(),
  categories: z.array(z.string()),
  salesChannels: z.array(z.string()),
  salesChannelsCustom: z.string(),
  socials: z.object({
    website: z.string(),
    facebook: z.string(),
    instagram: z.string(),
    youtube: z.string(),
    vk: z.string(),
    rutube: z.string(),
    tiktok: z.string(),
    other: z.string(),
  }),
})
export type ShopProfile = z.infer<typeof ShopProfileSchema>

export const RequisitesSchema = z.object({
  inn: z.string(),
  shortName: z.string(),
  fullName: z.string(),
  organizationForm: z.string(),
  legalAddress: z.string(),
  bankAccount: z.string(),
  bik: z.string(),
  kpp: z.string(),
  ogrn: z.string(),
  accountantContact: z.string(),
})
export type Requisites = z.infer<typeof RequisitesSchema>

export const CompanySuggestionSchema = z.object({
  inn: z.string(),
  shortName: z.string(),
  fullName: z.string(),
  legalAddress: z.string(),
  kpp: z.string(),
  ogrn: z.string(),
})
export type CompanySuggestion = z.infer<typeof CompanySuggestionSchema>

export const ShippingOriginSchema = z.object({
  city: z.string(),
  cityCode: z.number().int().optional(),
  postalCode: z.string(),
  address: z.string(),
  contactName: z.string(),
  contactPhone: z.string(),
})
export type ShippingOrigin = z.infer<typeof ShippingOriginSchema>

export const ProfileDocumentStatusSchema = z.enum([
  "pending",
  "uploaded",
  "approved",
  "rejected",
])
export type ProfileDocumentStatus = z.infer<typeof ProfileDocumentStatusSchema>

export const ProfileDocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  required: z.boolean(),
  fileName: z.string().optional(),
  uploadedAt: z.string().optional(),
  status: ProfileDocumentStatusSchema,
})
export type ProfileDocument = z.infer<typeof ProfileDocumentSchema>

export const FounderDataSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  position: z.string(),
  phone: z.string(),
  email: z.string(),
})
export type FounderData = z.infer<typeof FounderDataSchema>

/** Full seller onboarding submission used by the admin moderation dialog. */
export const SellerShopDetailSchema = SellerShopSchema.extend({
  survey: SurveyDataSchema.nullable(),
  shop: ShopProfileSchema,
  requisites: RequisitesSchema,
  documents: z.array(ProfileDocumentSchema),
  founders: z.array(FounderDataSchema),
})
export type SellerShopDetail = z.infer<typeof SellerShopDetailSchema>
